import React from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getResendVerificationCodeLink } from 'app/store/Auth/selectors';
import { addAlertAction } from 'app/store/Alerts/slice';
import { useFetch } from 'app/utils/hooks';
import { HStack, StackProps, Text } from '@chakra-ui/react';
import { signUpResendVerificationAction } from 'app/store/Mixpanel/slice';
import { ResendCodeResponse } from 'types/auth';

interface ResendCodeProps extends StackProps {
  label?: string;
  email?: string;
  username?: string;
  link?: string;
}

export const ResendCode: React.FC<ResendCodeProps> = ({
  label = "Didn't receive a code?",
  email,
  username,
  link,
  ...stackProps
}) => {
  const dispatch = useAppDispatch();
  const { callAPI, isLoading } = useFetch<
    { email?: string; username?: string },
    ResendCodeResponse
  >();
  const resendVerificationLink = useAppSelector(getResendVerificationCodeLink)
    ?.href;
  const linkToCall = link || resendVerificationLink;
  const resendCodeAction = async () => {
    if (!email && !username) {
      dispatch(
        addAlertAction({
          message: 'Please enter your email',
        })
      );
    } else if (linkToCall) {
      try {
        await callAPI(linkToCall, {
          email,
          username,
        });
        dispatch(
          addAlertAction({
            message: 'Your code has been sent. Please check your email',
          })
        );
        dispatch(signUpResendVerificationAction());
      } catch (e: any) {
        dispatch(
          addAlertAction({ message: 'Something went wrong. Please try again' })
        );
      }
    }
  };

  if (!linkToCall) {
    return null;
  }

  return (
    <HStack mx="auto" layerStyle="base2" {...stackProps}>
      <Text fontWeight={500}>{label}</Text>

      <Text
        as="button"
        color="B.80"
        fontWeight={500}
        ml={0.5}
        onClick={resendCodeAction}
        disabled={isLoading}
        _hover={{
          transform: 'scale(1.02)',
        }}
        _disabled={{
          opacity: 0.36,
          cursor: 'default',
        }}
        data-testid="resend-button"
      >
        Resend code
      </Text>
    </HStack>
  );
};
